<template>
  <div>
    <GoogleMap
      :mapId="useRuntimeConfig().public.apiGoogleMapId"
      :api-promise="apiPromise"
      :style="`width: 100%; height:${mapheight}`"
      v-show="!isMobile || (isMobile && showGoogleMap)"
      ref="mapRef"
      :center="{
        lat: mapCenter.lat ?? 0,
        lng: mapCenter.lng ?? 0,
      }"
      :zoom="mapZoom"
      :scrollwheel="false"
      :streetViewControl="false"
      :disableDefaultUI="false"
      :mapTypeControl="false"
      :fullscreenControl="false"
      :zoomControl="true"
      gestureHandling="cooperative"
      zoomControlPosition="LEFT_CENTER"
      :panControl="true"
    ></GoogleMap>
  </div>
</template>

<script setup>
import clusterIcon from "@/assets/icons/cluster.svg";
import pinIcon from "@/assets/icons/marker-pin.svg";
import SecondaryPinIcon from "@/assets/icons/marker-secondary-pin.svg";
import { useGlobalStore } from "@/stores/global";
import { useMapStore } from "@/stores/map";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { useGoogleMapsLoader } from "@/composables/useGoogleMapsLoader";
import { GoogleMap } from "vue3-google-map";
import { useCheckoutStore } from "~/stores/checkout";
import { useGetStartedModalStore } from "~/stores/get-started-modal";
import { useProductStore } from "~~/stores/filter";

const globalStore = useGlobalStore();
const mapStore = useMapStore();
const getStartedStore = useGetStartedModalStore();
const productStore = useProductStore();
const checkoutStore = useCheckoutStore();
const emit = defineEmits(["getClinicsList", "markersReady", "clinicsListFailed"]);
const mapRef = ref(null);
const locations = ref([]);
const locationsWithCountryMarkers = ref([]);
const mapCenter = ref({
  lat: 0,
  lng: 0,
});
const country = ref(globalStore.siteCountry);
const showInfoWindow = ref(false);
const InfoWindowContent = ref("");
const currentInfoWindow = ref();
const locale = computed(() => globalStore.siteCurrentLocale);
const markerClusterSettings = computed(() => globalStore.siteClusterSettings);
const route = useRoute();
const isNurseStationServiceEnabled = computed(() => globalStore.siteNurseStationServiceEnabled);

const { t } = useI18n();
const loader = useGoogleMapsLoader();
const apiPromise = loader.load();
const props = defineProps({
  city: {
    type: String,
    default: "",
  },
  range: {
    type: Number,
  },
  zoom: {
    type: Number,
  },
  onSearchShowAllCity: {
    type: Boolean,
    default: false,
  },
  isMobile: {
    type: Boolean,
  },
  showGoogleMap: {
    type: Boolean,
  },
  testId: {
    type: Number,
  },
  mapheight: {
    type: String,
    default: "844px",
  },
  citySlug: {
    type: String,
  },
  // { lat, lng } from server/data/city-coordinates.json. When set, the city isn't
  // geocoded in the browser, so the map queries the same point as the SSR clinic list.
  cityCoordinates: {
    type: Object,
    default: null,
  },
  cityTitle: {
    type: String,
  },
  categoryId: {
    default: "",
  },
  serviceType: {
    type: String,
    default: "",
  },
  consultationClinic: {
    type: Boolean,
    default: false,
  },
  isClinicPage: {
    type: Boolean,
    default: false,
  },
  isCityPage: {
    type: Boolean,
    default: false,
  },
  clinicTypeFilter: {
    type: Array,
    default: () => [],
  },
  showClinicLocation: {
    type: Boolean,
    default: false,
  },
  clinicData: {
    type: Object,
    default: null,
  },
  isGetStartedModal: {
    type: Boolean,
    default: false,
  },
});

const isNurseStation = (location) => location?.clinic_type == "nurse-station";

function buildPinElement(location) {
  if (location.clinics) {
    const wrap = document.createElement("div");
    wrap.className = "b2k-pin-country";

    const img = document.createElement("img");
    img.src = `/icons/flags-map/${getCountryCode(location)}.svg`;
    img.width = 32;
    img.height = 32;
    img.draggable = false;
    wrap.appendChild(img);

    const label = document.createElement("span");
    label.className = "marker-label";
    label.textContent = location.clinics;
    wrap.appendChild(label);
    return wrap;
  }

  // 99% case — single <img>, no wrapper div, no inline style. Halves the per-pin node count and shifts styling work to the browser's CSS engine.
  const img = document.createElement("img");
  img.src = isNurseStation(location) && isNurseStationServiceEnabled.value ? SecondaryPinIcon : pinIcon;
  img.className = "b2k-pin";
  img.draggable = false;
  return img;
}

function buildClusterElement(count) {
  const wrap = document.createElement("div");
  wrap.className = "b2k-cluster";
  wrap.style.cssText = `width:40px;height:40px;background:url(${clusterIcon}) center/contain no-repeat;display:flex;align-items:center;justify-content:center;color:white;font-size:16px;font-weight:bold;cursor:pointer;`;
  wrap.textContent = String(count);
  return wrap;
}

const clusterRenderer = {
  render({ count, position }, _stats, map) {
    return new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      content: buildClusterElement(count),
      zIndex: 1000 + count,
    });
  },
};

let cluster = null;
// Keyed by location object identity so unchanged locations across a `locations.value` reassignment reuse their existing marker.
const markerByLocation = new Map();

function buildMarker(location) {
  const m = new google.maps.marker.AdvancedMarkerElement({
    position: { lat: location.lat, lng: location.lng },
    content: buildPinElement(location),
    gmpClickable: true,
  });
  m.addListener("gmp-click", () => onWindowClick(location));
  return m;
}

function teardownAll() {
  if (cluster) cluster.clearMarkers();
  for (const m of markerByLocation.values()) m.map = null;
  markerByLocation.clear();
}

function runRebuild() {
  if (!mapRef.value?.ready || !mapRef.value?.map) return;

  // Skip NaN-coord entries — legacy Marker rendered those at (0,0), AdvancedMarkerElement throws and aborts the whole batch.
  const nextLocations = locations.value.filter((l) => Number.isFinite(l.lat) && Number.isFinite(l.lng));
  const nextSet = new Set(nextLocations);

  const toRemove = [];
  for (const [location, marker] of markerByLocation) {
    if (!nextSet.has(location)) {
      toRemove.push(marker);
      markerByLocation.delete(location);
    }
  }

  const toAdd = [];
  for (const location of nextLocations) {
    if (!markerByLocation.has(location)) {
      const marker = buildMarker(location);
      markerByLocation.set(location, marker);
      toAdd.push(marker);
    }
  }

  if (markerClusterSettings.value.showCluster) {
    if (!cluster) {
      cluster = new MarkerClusterer({
        map: mapRef.value.map,
        markers: Array.from(markerByLocation.values()),
        algorithm: new SuperClusterAlgorithm({ maxZoom: markerClusterSettings.value.maxClusterZoom }),
        renderer: clusterRenderer,
      });
    } else {
      if (toRemove.length) cluster.removeMarkers(toRemove);
      if (toAdd.length) cluster.addMarkers(toAdd);
    }
  } else {
    for (const m of toRemove) m.map = null;
    for (const m of toAdd) m.map = mapRef.value.map;
  }
}

// Defer the rebuild past the current render cycle so the browser paints the new sidebar / loader state before the heavy work blocks the main thread. Double-rAF guarantees one frame has been committed before we hand control back. Multiple rapid `locations` updates coalesce into one rebuild via the `scheduled` flag.
let scheduled = false;
function scheduleRebuild() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scheduled = false;
      runRebuild();
      emit("markersReady");
    });
  });
}

watch([() => mapRef.value?.ready, locations], scheduleRebuild, { immediate: true });
onBeforeUnmount(teardownAll);

const getCountryCode = (location) => {
  const firstSite = location.sites?.[0];
  if (!firstSite || !firstSite.handle) return null;

  const parts = firstSite.handle.split("-");
  return parts[1] ?? null;
};

const runtimeConfig = useRuntimeConfig();
const getLangUrl = (code) => {
  return runtimeConfig.public.i18n?.baseUrl + "/" + code;
};

function onWindowClick(location) {
  if (props.showClinicLocation) {
    return;
  }

  if (currentInfoWindow.value != null) {
    currentInfoWindow.value.close();
  }

  let title = location?.title;
  let subtitle = "";
  let icon = '<img loading="lazy" src="/icons/clinic-icon.svg" alt=""/>';
  let buttonColorClass = "bg-secondary hover:bg-[#B73983] focus:bg-[#B73983]";

  if (isNurseStation(location) && isNurseStationServiceEnabled) {
    buttonColorClass = "bg-primary hover:bg-[#00a1aa99] focus:bg-[#00a1aa99]";
  }

  let link =
    `<button  onclick="openPanel('${location?.title}', ${location?.id}, '${location?.slug}')" type="button" 
        class="relative h-9 select-none flex my-3 gap-2
          flex-nowrap ml-auto rounded font-bold focus:outline-none
          justify-center items-center duration-500 transition-all
          ease-in-out text-white 
          ${buttonColorClass}
          disabled:pointer-events-none disabled:opacity-60 w-full">` +
    t("common.btnSelect") +
    `</button>`;

  if (location.sites) {
    title = t("clinic.mapInfoWindow.title", { country: location?.title });
    const hasUkHandle =
      Array.isArray(location.sites) && location.sites.some((site) => site.handle?.toLowerCase() === "en-gb");
    const subtitleKey = hasUkHandle ? "clinic.mapInfoWindow.subtitleUK" : "clinic.mapInfoWindow.subtitle";
    subtitle =
      `<h4 class="text-primary font-semibold mt-2 mb-4">` +
      t(subtitleKey, {
        country: location?.title,
        clinics: location?.clinics,
      }) +
      `</h4>`;
    icon = ``;

    link = location.sites
      .map((site, index, arr) => {
        const label = arr.length === 1 ? t("clinic.mapInfoWindow.buttonViewClinics") : site.language_title;
        var href = getLangUrl(site.handle);
        return `
          <a href="${href}" >
          <button
            type="button" 
            class="relative h-9 select-none flex my-3 gap-2
              flex-nowrap ml-auto rounded font-bold focus:outline-none
              justify-center items-center duration-500 transition-all ease-in-out
              bg-secondary text-white hover:bg-[#B73983] focus:bg-[#B73983]
              disabled:pointer-events-none disabled:opacity-60 w-full"
          >
            ${label}
            <img src="/icons/circle-e-right-white.svg" alt="" class="w-5" width="20px"/>
          </button>
          </a>
        `;
      })
      .join("");
  }

  const contentString =
    `<div class="flex flex-col items-start bg-secondary-lighter relative mt-2  mx-[18px]">
             <button onclick="closeModal()" type="button"
               class="absolute top-0 -right-2 text-gray-400 bg-light-silver rounded-full fill-secondary  p-0.5 ml-auto inline-flex ">
               <svg aria-hidden="true" class="w-5 h-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                 <path fill-rule="evenodd"
                   d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                   clip-rule="evenodd"></path>
               </svg></button>
             <div class="flex items-start mt-2">
             ` +
    icon +
    `
             <div class="pl-3">
               <h3 class="pr-6"> ` +
    title +
    ` </h3>
             ` +
    subtitle +
    `
               <p class="pr-3 text-blog-14-sm my-2">${location?.content} </p>
             ` +
    link +
    `
            </div>
          </div>
              
            </div>`;

  const infowindow = new mapRef.value.api.InfoWindow({
    content: contentString,
  });
  infowindow.setOptions({
    position: location,
    pixelOffset: new google.maps.Size(0, -30),
    maxWidth: 300,
  });
  infowindow.open({
    map: mapRef.value.map,
  });

  window.closeModal = () => {
    currentInfoWindow.value.close();
  };

  window.openPanel = async (title, id, slug) => {
    productStore.clearAllFilters();
    // const { data: productsList } = await useFetchClinicTests(id);
    // mapStore.getTests(productsList.value.data);
    const location = {
      id,
      title,
      slug,
    };
    const pathWithoutLocale = route.path.replace(`/${locale.value}`, "").split("/")[1];

    switch (pathWithoutLocale) {
      case "get-started":
        await navigateTo({
          ...route,
          query: {
            ...route.query,
            slug,
          },
        });
        getStartedStore.navigate("show_tests");
        break;

      case "tests":
        mapStore.setSelectedClinic(location);
        return navigateTo(
          localeUrlQuery({
            query: {
              ...route.query,
              slug: location.slug,
              ...(location.content ?? { content: location.content }),
            },
          })
        );
        break;

      case "clinics":
        //mapStore.openPanel();
        navigateTo(
          localeUrlQuery({
            query: {
              ...route.query,
              slug: location.slug,
              ...(location.content ?? { content: location.content }),
            },
          })
        );
        break;

      default:
        checkoutStore.setSelectedClinicId(location.id);
        return navigateTo(
          localeUrlQuery({
            query: {
              ...route.query,
              slug: location.slug,
              ...(location.content ?? { content: location.content }),
            },
          })
        );
    }
  };

  currentInfoWindow.value = infowindow;
}

watch(
  () => mapStore.getSelectedClinic,
  (n) => {
    showInfoWindow.value = true;
    InfoWindowContent.value = n;
  },
  { deep: true }
);

const mapZoom = computed(() => {
  if (props.zoom) {
    return Number(props.zoom);
  } else if (props.city != "all" && props.city != "clearSearch" && !props.zoom) {
    return Number(globalStore.siteSearchZoom);
  } else if (props.isMobile) {
    return Number(globalStore.siteDefaultZoomMobile);
  }
  return Number(globalStore.siteDefaultZoom);
});

function resetMapCenter() {
  mapCenter.value = {
    lat: globalStore.siteMapConfigLatLng.latitude,
    lng: globalStore.siteMapConfigLatLng.longitude,
  };
}

watch(
  () => mapRef.value?.ready,
  async (ready) => {
    let mapPositionValue = {};
    if (!ready) return;
    if (props.citySlug || props.showClinicLocation) {
      mapPositionValue = Object.assign(mapPositionValue, await updateMapPosition(props, mapRef));
      await getClinicList(mapPositionValue);
    }
  }
);

watch(
  () => props.city,
  async (newData, oldData) => {
    if (newData === oldData) return;
    let mapPositionValue = {};
    resetMapCenter();
    try {
      switch (newData) {
        case "clearSearch":
          if (props.isClinicPage) {
            locations.value = locationsWithCountryMarkers.value;
          }
          mapPositionValue = Object.assign(mapPositionValue, globalStore.siteMapConfigLatLng);
          break;
        default:
          if (((props.onSearchShowAllCity && newData !== "") || !props.onSearchShowAllCity) && mapRef.value?.ready) {
            mapPositionValue = Object.assign(mapPositionValue, await updateMapPosition(props, mapRef));
            await getClinicList(mapPositionValue);
          }
          break;
      }
    } catch (err) {
      console.error("[GoogleMap] clinic search failed:", err);
    } finally {
      // Ensure the parent's loader gets a `markersReady` signal even when locations didn't change (no-op path) or an error was caught — otherwise `isSearching` stays stuck on.
      scheduleRebuild();
    }
  }
);

watch(
  () => props.clinicTypeFilter,
  async () => {
    if (mapRef.value?.ready) {
      const mapPositionValue = await updateMapPosition(props, mapRef);
      await getClinicList(mapPositionValue);
    }
  }
);

/**
 * Writes `service_type` onto a clinic-list query.
 *
 * Get started → clinic test is the one flow that needs *both* clinic-test and instant-test
 * clinics on the map; we omit the filter so the backend returns the union. Every other
 * caller keeps the single-value `service_type` filter.
 */
function applyServiceTypeParam(target) {
  if (!props.serviceType) return;
  if (props.isGetStartedModal && props.serviceType === "clinic-test") return;
  target.service_type = props.serviceType;
}

if (props.city == "all") {
  let mapPositionValue = {};
  mapPositionValue = Object.assign(mapPositionValue, globalStore.siteMapConfigLatLng);
  applyServiceTypeParam(mapPositionValue);
  await getClinicList(mapPositionValue);
}
// fetch clinic list api
const updateMapPosition = async (props, mapRef) => {
  if (props.showClinicLocation && props.clinicData) {
    return {
      latitude: Number(props.clinicData.latitude),
      longitude: Number(props.clinicData.longitude),
      range: props.range || globalStore.siteSearchRange,
    };
  }
  // City page with known coordinates: the exact query the page fetched during SSR, so list and map match.
  if (!props.city && props.cityCoordinates) {
    return buildCityClinicParams({
      coordinates: props.cityCoordinates,
      range: props.range,
      defaultRange: globalStore.siteSearchRange,
      clinicTypes: isNurseStationServiceEnabled.value ? props.clinicTypeFilter : [],
    });
  }

  const updatedValues = {};

  if (props.categoryId && props.serviceType) {
    updatedValues.category = props.categoryId;
    applyServiceTypeParam(updatedValues);
  }

  if (isNurseStationServiceEnabled.value && props.clinicTypeFilter && props.clinicTypeFilter.length > 0) {
    props.clinicTypeFilter.forEach((type, index) => {
      updatedValues[`clinic_type[${index}]`] = type;
    });
  }

  if (props.consultationClinic) {
    updatedValues.consultation_clinic = 1;
  }

  if (props.testId) {
    updatedValues["tests[]"] = props.testId;
  }

  if (props.city || props.citySlug) {
    const range = props.range || globalStore.siteSearchRange;
    updatedValues.latitude = 0;
    updatedValues.longitude = 0;
    updatedValues.range = range;

    let city = props.city ? props.city : props.citySlug;
    city = await findAndReplaceSearchTerm(city);

    const geoCoder = new mapRef.value.api.Geocoder();
    try {
      const result = await geoCoder.geocode({
        address: city + ", " + country.value,
      });

      if (result) {
        const { results } = result;
        const resultData = results[0].geometry.location;
        updatedValues.latitude = resultData.lat();
        updatedValues.longitude = resultData.lng();
      }
    } catch (err) {
      console.log(err.message);
    }
  }

  return updatedValues;
};

const MAX_CLINIC_LIST_ATTEMPTS = 3;

async function getClinicList(mapPositionValue) {
  if (
    isNurseStationServiceEnabled.value &&
    (props.isClinicPage || props.isCityPage) &&
    Array.isArray(props.clinicTypeFilter) &&
    props.clinicTypeFilter.length === 0
  ) {
    locations.value = [];
    emit("getClinicsList", { data: "empty" });
    return;
  }

  if (props.showClinicLocation) {
    if (!props.clinicData) {
      locations.value = [];
      return;
    }

    const clinic = {
      id: props.clinicData.id,
      title: props.clinicData.display_title,
      slug: props.clinicData.slug ?? route.params.slug,
      content: props.clinicData.address,
      lat: Number(props.clinicData.latitude),
      lng: Number(props.clinicData.longitude),
    };

    locations.value = [clinic];
    mapCenter.value = { lat: clinic.lat, lng: clinic.lng };

    return;
  }

  if (mapPositionValue.latitude == 0 && mapPositionValue.longitude == 0) {
    resetMapCenter();
  } else {
    mapCenter.value = {
      lat: mapPositionValue.latitude,
      lng: mapPositionValue.longitude,
    };
  }

  // On city pages a failed request is retried and then reported (clinicsListFailed), keeping any SSR list. Other pages
  // (clinic search, get-started modal, test pages) keep the error page they always had on a failed request.
  const clinicListResponse = await useClinicListsByCity(mapPositionValue, {
    locale: locale.value,
    useErrorBoundary: !props.isCityPage,
  });
  // refresh()-based, so it also recovers when the city page's SSR fetch failed: that error sits in the page payload
  // under the same key, and a plain repeat call would just return it. Previously this retried without limit.
  if (!(await retryUntilData(clinicListResponse, { attempts: MAX_CLINIC_LIST_ATTEMPTS }))) {
    console.error("[GoogleMap] clinic list request returned no data", mapPositionValue);
    emit("clinicsListFailed");
    return;
  }
  const clinicList = clinicListResponse.data;

  let city = props.city ? props.city : props.citySlug;
  const siteMarkers = props.isClinicPage && city == "all" ? await useClinicSiteMarkers() : null;

  const entries = [
    ...(clinicList.value?.data ?? []),
    ...(props.isClinicPage && city == "all" && siteMarkers?.data?.value ? siteMarkers.data.value.data : []),
  ];

  let result = entries.map(toClinicListItem);

  if (
    !props.onSearchShowAllCity ||
    (props.onSearchShowAllCity && (props.city == "all" || props.city == "clearSearch"))
  ) {
    if (props.isClinicPage) {
      locationsWithCountryMarkers.value = result;
    }
    locations.value = result;
  } else if (isNurseStationServiceEnabled.value && props.isClinicPage) {
    let allLocations = locations.value;

    //storing locations from locationsWithCountryMarkers
    if (locations.value.length < locationsWithCountryMarkers.value.length) {
      allLocations = locationsWithCountryMarkers.value;
    }

    let filteredResult = allLocations.filter((location) => !location.sites);
    filteredResult = filteredResult.filter((location) => props.clinicTypeFilter.includes(location.clinic_type));

    locations.value = filteredResult;
  } else if (props.isClinicPage) {
    const filteredResult = locations.value.filter((location) => !location.sites);
    locations.value = filteredResult;
  }

  if (result && result.length === 0) {
    emit("getClinicsList", { data: "empty" });
  } else {
    emit("getClinicsList", result);
  }
}

async function findAndReplaceSearchTerm(searchTerm) {
  let relocators = globalStore.siteSettings.relocators;
  for (let i = 0; i < relocators.length; i++) {
    if (relocators[i].city_name_search.includes(searchTerm.toLowerCase())) {
      searchTerm = relocators[i].city_name_replace;
    }
  }
  return searchTerm;
}

onMounted(() => {
  mapCenter.value = {
    lat: globalStore.siteMapConfigLatLng.latitude,
    lng: globalStore.siteMapConfigLatLng.longitude,
  };
  mapStore.mapLoaded += 1;
});
</script>

<style>
/* Imperative marker DOM lives in Google Maps' overlay container, outside Vue's scope — these rules must be global. */
/* `contain` caps style/layout/paint propagation per pin — caps WebKit's per-element cost when the cluster updates many at once. */
.b2k-pin {
  display: block;
  cursor: pointer;
  contain: layout style paint;
}

.b2k-pin-country {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  contain: layout style paint;
}

.b2k-pin-country img {
  display: block;
}

.b2k-cluster {
  contain: layout style paint;
}
</style>

<style scoped>
:deep(.gm-style-iw) {
  background: #f7f7f7;
  box-shadow: 0px 1px 6px #00000066;
  border-radius: 5px;
  padding: 0px;
}

:deep(.gm-style-iw-chr) {
  display: none;
}

:deep(.gm-style-iw-d) {
  background: #f7f7f7;
  overflow: auto !important;
}

:deep(::-webkit-scrollbar-track) {
  background: white;
}

:deep(.gm-style .gm-style-iw-tc::after) {
  background: #f7f7f7;
}

:deep(button.gm-ui-hover-effect) {
  color: rgb(156 163 175);
  padding: 0.375rem;
  background-color: rgb(216 216 216) !important;
  border-radius: 9999px;
  visibility: hidden;
}
</style>
